import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Input, Button, Spin, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import FormattedText from './FormattedText';
import './aiChat.scss';

const MAX_QUESTION_LENGTH = 200;

const AiChat = ({
    chatHistory,
    onSend,
    isLoading,
    questionLimit,
    questionsAsked,
    isRateLimited,
    rateLimitMessage,
}) => {
    const [question, setQuestion] = useState('');

    const handleSend = () => {
        if (!question.trim()) return;
        if (question.length > MAX_QUESTION_LENGTH) {
            message.warning(`Question must be under ${MAX_QUESTION_LENGTH} characters`);
            return;
        }
        onSend(question);
        setQuestion('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleCopyAnswer = (answer) => {
        window.navigator.clipboard.writeText(answer).then(() => {
            message.success('Copied to clipboard');
        }).catch(() => {
            message.error('Failed to copy');
        });
    };

    const disabled = (questionLimit !== null && questionsAsked >= questionLimit) || isRateLimited;
    const charactersRemaining = MAX_QUESTION_LENGTH - question.length;

    return (
        <div className="ai-chat-wrapper">
            <div className="chat-history">
                {chatHistory.map((item) => (
                    <div key={item.id} className="chat-item">
                        <div className="chat-question">{item.question}</div>
                        <div className="chat-answer-wrapper">
                            <div className="chat-answer">
                                <FormattedText text={item.answer} />
                            </div>
                            <button
                                type="button"
                                className="copy-btn"
                                onClick={() => handleCopyAnswer(item.answer)}
                                title="Copy answer">
                                <CopyOutlined />
                            </button>
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="loading-wrapper">
                        <Spin size="small" />
                        <span className="loading-text">AI is thinking...</span>
                    </div>
                )}
            </div>
            {disabled && (
                <div className="upgrade-msg">
                    {isRateLimited ? (
                        <span>{rateLimitMessage}</span>
                    ) : (
                        <>
                            <span>Question limit reached.</span>
                            <p className="upsell-text">
                                Want more personalized insights? Book a consultation with Prakash Astrologer.
                            </p>
                            <Link to="/consultations" className="upsell-btn">
                                Book Consultation
                            </Link>
                        </>
                    )}
                </div>
            )}
            <div className="chat-input-row">
                <Input
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={disabled || isLoading}
                    placeholder="Ask a question about your horoscope..."
                    maxLength={MAX_QUESTION_LENGTH} />
                <Button
                    type="primary"
                    onClick={handleSend}
                    disabled={disabled || !question.trim() || isLoading}
                    loading={isLoading}>
                    Send
                </Button>
            </div>
            <div className="chat-footer">
                {questionLimit !== null && (
                    <div className="question-counter">
                        {questionsAsked}/{questionLimit} questions used
                    </div>
                )}
                <div className={`char-counter ${charactersRemaining < 20 ? 'warning' : ''}`}>
                    {charactersRemaining} characters remaining
                </div>
            </div>
            <div className="ai-disclaimer">
                AI responses may be inaccurate. Please check important info.
            </div>
        </div>
    );
};

AiChat.propTypes = {
    chatHistory: PropTypes.array,
    onSend: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    questionLimit: PropTypes.number,
    questionsAsked: PropTypes.number,
    isRateLimited: PropTypes.bool,
    rateLimitMessage: PropTypes.string,
};

AiChat.defaultProps = {
    chatHistory: [],
    isLoading: false,
    questionLimit: null,
    questionsAsked: 0,
    isRateLimited: false,
    rateLimitMessage: '',
};

export default AiChat;
